/* ============================================================================
   numerals.ts — Eastern Arabic numerals, and every other locale-sensitive
   number the product renders.

   Decision D-02: Arabic uses EASTERN ARABIC NUMERALS  ٠١٢٣٤٥٦٧٨٩  (U+0660..0669),
   not the Western digits an English build shows. That decision reaches further
   than the digits themselves — the decimal separator, the thousands separator
   and the percent sign all change too, and getting the digits right while
   leaving a Western full stop as the decimal point is the usual half-done job:

       ٪٨٥٫٥        correct     (Arabic decimal U+066B, percent U+066A)
       85.5%        correct     (English)
       ٨٥.٥%        WRONG       (Arabic digits, Western punctuation)

   Everything here goes through Intl on the locale `ar-IQ-u-nu-arab`. Do not
   hand-roll a digit substitution: it gets the digits and nothing else.
   ========================================================================= */

export type AppLocale = 'en' | 'ar'

/** Iraq, Eastern Arabic numbering system. The `-u-nu-arab` extension is what
 *  selects the numerals; without it ar-IQ renders Western digits. */
export const INTL_LOCALE: Record<AppLocale, string> = {
  en: 'en-GB',
  ar: 'ar-IQ-u-nu-arab',
}

const numberFmt = new Map<string, Intl.NumberFormat>()
const dateFmt = new Map<string, Intl.DateTimeFormat>()

function nf(locale: AppLocale, opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = locale + JSON.stringify(opts)
  let f = numberFmt.get(key)
  if (!f) { f = new Intl.NumberFormat(INTL_LOCALE[locale], opts); numberFmt.set(key, f) }
  return f
}

function df(locale: AppLocale, opts: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat {
  const key = locale + JSON.stringify(opts)
  let f = dateFmt.get(key)
  if (!f) { f = new Intl.DateTimeFormat(INTL_LOCALE[locale], opts); dateFmt.set(key, f) }
  return f
}

/** A plain count: rooms, tasks, pieces. */
export const formatCount = (n: number, locale: AppLocale) => nf(locale).format(n)

/** A decimal, with an explicit number of places so a column does not ragged. */
export const formatDecimal = (n: number, locale: AppLocale, places = 1) =>
  nf(locale, { minimumFractionDigits: places, maximumFractionDigits: places }).format(n)

/**
 * **A WORKLOAD CREDIT. TWO PLACES, ALWAYS.**
 *
 * DEFECT #112, AND IT IS WORTH THE WHOLE OF THIS COMMENT, because the symptom is the exact
 * failure credits exist to prevent.
 *
 * Credits have been `DECIMAL(4,2)` on the wire since Sprint 6, and every screen drew them
 * with `formatDecimal`, which is ONE place. That was invisible for twenty-six sprints
 * because the only credit values the product could produce were the design pack's four —
 * 1.5, 1.0, 3.0 and 0.5 — and every one of them fits in one place. Sprint 32 makes two
 * places ordinary: a suite at 45 minutes over a divisor of 20 is 2.25, and at a divisor of
 * 15 a 20-minute touch-up is 1.33.
 *
 * WHAT THAT DID TO THE ASSIGNMENT BOARD. Two such rooms in one lane: each card drew "2.3",
 * and the lane total — summed in hundredths by `sumCredits`, correctly — drew "4.5". A
 * supervisor balancing a round adds the cards, gets 4.6, and the column does not add up.
 * There is no reading of that screen under which the supervisor is wrong, and nothing
 * anywhere tells them which figure to trust. A number nobody can add is worse than no
 * number.
 *
 * SO IT IS TWO PLACES AND FIXED. "1.50" beside "2.25" costs one character of noise on the
 * round values; a column that does not add up costs the supervisor's belief in the board,
 * which is the only thing making them use it. `tabular-nums` is already on every cell that
 * draws one, so the column still aligns.
 *
 * AND IT IS ITS OWN FUNCTION rather than a changed default on `formatDecimal`, because
 * "how a credit is written" is a rule of this product and the percentage of a budget is
 * not. Naming it is what stops the next screen drawing a credit some other way.
 */
export const formatCredits = (n: number, locale: AppLocale) =>
  nf(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

/**
 * A QUANTITY OF THINGS — 408 towels, or 2.5 sets.
 *
 * Decimals only when there are any, which is the opposite of `formatDecimal`'s rule and
 * right for a different reason. Credits are always shown to TWO places so a column of
 * them adds up on the page — the function two lines above says why, and this sentence
 * said "one place" until Sprint 45, contradicting it. A comment that disagrees with the
 * code it points at is worse than none: somebody eventually believes the comment and
 * writes a screen to match it. Stock is overwhelmingly whole, and a column reading "408.0,
 * 535.0, 380.0" is three characters of noise on every row that says nothing. Where a
 * half-set genuinely exists, it still shows as 2.5.
 */
export const formatQuantity = (n: number, locale: AppLocale) =>
  nf(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)

/** A percentage. Pass 0.855, not 85.5 — Intl does the multiplication and
 *  supplies the correct percent sign for the locale (٪ in Arabic). */
export const formatPercent = (fraction: number, locale: AppLocale, places = 0) =>
  nf(locale, { style: 'percent', minimumFractionDigits: places, maximumFractionDigits: places })
    .format(fraction)

/** A duration in minutes, rendered as the product shows it: "1h 20m" / "١ س ٢٠ د". */
export function formatMinutes(mins: number, locale: AppLocale): string {
  const h = Math.floor(mins / 60)
  const m = Math.round(mins % 60)
  const H = formatCount(h, locale)
  const M = formatCount(m, locale)

  /* A WHOLE NUMBER OF HOURS DROPS THE MINUTES. "every 8h 0m" and "3h 0m late" were on
     every row of the area board, and a person reading them says "every eight hours" —
     the trailing zero is noise repeated forty times down a page. It was invisible until
     the board was rendered, because until then the only durations in the product were
     cleaning times, which are never round. */
  if (locale === 'ar') return h ? (m ? `${H} س ${M} د` : `${H} س`) : `${M} د`
  return h ? (m ? `${H}h ${M}m` : `${H}h`) : `${M}m`
}

/**
 * Clock time. 24-hour in both locales — housekeeping rosters are 24-hour.
 *
 * `timeZone` IS FOR THE ONE READER WHOSE PHONE MIGHT BE WRONG: a guest.
 *
 * Everybody else using this product is a member of staff standing in the building, whose
 * device is on the hotel's time; passing nothing is right for them and stays right. A
 * guest is a pilgrim who landed four hours ago, and their phone may still be on the time
 * where they boarded — so "somebody will be with you by 09:15" would be four hours out,
 * and wrong in the direction that makes the hotel look late. Given the property's zone,
 * the browser's own tz database does the rest.
 */
export const formatTime = (d: Date, locale: AppLocale, timeZone?: string) =>
  df(locale, {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(d)

/**
 * **A TIME OF DAY HELD AS MINUTES PAST MIDNIGHT**, written the way this product writes a
 * clock: 24-hour, in the reader's own digits.
 *
 * The model stores several of these — `Shift.StartMinuteLocal` since Sprint 16 and
 * `Property.GenerateWorkAtLocalMinute` since Sprint 34 — because a hotel's six o'clock is
 * six o'clock every morning, including the two days a year when it is not the same number
 * of hours after midnight UTC. An instant would drift; minutes do not.
 *
 * **IT GOES THROUGH `formatTime` RATHER THAN BUILDING A STRING**, which is the whole point
 * of it existing. `${h}:${m.padStart(2,'0')}` looks obviously correct and is wrong in
 * Arabic — Western digits in a line of Arabic-Indic ones — and it is the shape of #76,
 * where a value was assembled by hand instead of being handed to `Intl` whole.
 *
 * The date is arbitrary and UTC on both sides, so nothing can shift it.
 */
export const formatClockMinute = (minute: number, locale: AppLocale) =>
  formatTime(
    new Date(Date.UTC(2000, 0, 1, Math.floor(minute / 60), minute % 60)), locale, 'UTC')

export const formatDate = (d: Date, locale: AppLocale) =>
  df(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(d)

/** The weekday alone, short: "Sat" / "السبت". For a grid whose columns are days. */
export const formatWeekday = (d: Date, locale: AppLocale) =>
  df(locale, { weekday: 'short' }).format(d)

/** Day and month, no year — a column header in a week that is obviously this year. */
export const formatDayMonth = (d: Date, locale: AppLocale) =>
  df(locale, { day: 'numeric', month: 'short' }).format(d)

export const formatDateTime = (d: Date, locale: AppLocale) =>
  df(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .format(d)

/* ---- identifiers are NOT localised --------------------------------------
   A room number, a task reference, a reservation reference and a barcode are
   identifiers, not quantities. Room 412 is called 412 in both languages: it is
   painted on the door, it is what a supervisor says out loud, and rendering it
   as ٤١٢ on the handset while the door says 412 is a real operational error,
   not a cosmetic one.

   Identifiers keep Western digits AND are wrapped in an isolated left-to-right
   run so that Arabic bidirectional reordering cannot reverse or re-sequence
   them inside a sentence. Use <Identifier> from components/ui/identifier.tsx
   rather than calling this directly. */
export const IDENTIFIER_IS_NOT_LOCALISED = true

/** Direction for a locale. Everything else follows from this one value. */
export const dirFor = (locale: AppLocale): 'ltr' | 'rtl' => (locale === 'ar' ? 'rtl' : 'ltr')

/* ── reading a number back ────────────────────────────────────────────────── */

/**
 * PARSE A QUANTITY SOMEBODY TYPED. The mirror of `formatQuantity`, and the first
 * place in this product where a number travels in the other direction.
 *
 * IT ACCEPTS EASTERN ARABIC DIGITS, and that is the whole reason it exists rather
 * than a `Number(value)` at the call site. What a numeric keypad produces is not our
 * decision: most Arabic keyboards on Android and iOS emit Western digits, some emit
 * ٠١٢٣٤٥٦٧٨٩, and a third-party keyboard may emit either. A store keeper who types
 * ١٢ and is told "enter a number" concludes the application does not speak Arabic,
 * and they are not wrong.
 *
 * Both Arabic decimal separators are taken too — the Arabic decimal U+066B, and the
 * Western full stop, because a phone keypad's "." key produces the latter regardless
 * of the language on screen.
 *
 * Returns NULL, never NaN. NaN propagates silently through arithmetic and arrives at
 * the server as a quantity of nothing; null forces the caller to say what an
 * unreadable entry means.
 */
export function parseQuantity(value: string): number | null {
  const normalised = value
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    // Arabic-Indic digits as used in Persian/Urdu keyboards. Same numbers, different
    // code points, and a keyboard we do not control may well produce them.
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/[٫٬,]/g, (s) => (s === '٬' || s === ',' ? '' : '.'))
    .replace(/‏|‎|\s/g, '')
    .trim()

  if (normalised === '') return null
  // Deliberately strict: `Number` accepts '0x10', '1e5' and Infinity, none of which
  // are a quantity of towels anybody meant to type.
  if (!/^-?\d*\.?\d*$/.test(normalised) || !/\d/.test(normalised)) return null

  const parsed = Number(normalised)
  return Number.isFinite(parsed) ? parsed : null
}
